/**
 * What a city hub says when it has nothing to book (owner-approved concept
 * "28 · Empty places that still help").
 *
 * It replaced a bare "No activities found" that still offered links to more
 * empty pages. Three things instead:
 *
 * 1. An honest line: nothing to book here yet.
 * 2. Up to six real, bookable activities from the nearest cities that have
 *    some. How "nearest" is decided lives in `pickNearbyBookableCities`.
 * 3. Two ways to get a plan anyway: WhatsApp with our team, or MaJourneys AI
 *    with the question already asked.
 *
 * The panel is a surface you read, so it wears the approved pewter band; the
 * two actions are controls, so the primary is the navy gradient and the
 * secondary is white with one navy hairline.
 */
import { Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PW, pewterPanelApproved, pewterPrimary } from "@majourneys/booking";
import { WHATSAPP_URL } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";

import {
  emptyHubCitiesQueryOptions,
  nearbyRailQueryOptions,
  type NearbyRailItem,
} from "../api/activities-query";
import { pickNearbyBookableCities } from "../lib/nearby-bookable-cities";
import { ActivityCardDiscover } from "./activity-card-discover";

/** Enough to show it is a real choice, few enough to stay a rail. */
const RAIL_MAX = 6;

/**
 * Cities where dinner is sold as a dinner show, so an empty Restaurants hub
 * there points at the Shows hub instead of saying "nothing here" (owner,
 * 2026-10-04, for Agafay's Alkamar and Le Bédouin dinner shows).
 *
 * A link, not a move: putting the shows in the Restaurants category would give
 * them the free table form and drop their priced packages.
 */
export const DINNER_SHOW_CITIES: ReadonlySet<string> = new Set(["agafay"]);

export interface EmptyCityHelpProps {
  lang: string;
  citySlug: string;
  cityName: string;
  /** Set on a city + category hub; the heading then names the category. */
  categoryName?: string;
  categorySlug?: string;
}

/** The WhatsApp chat with our team, the first message already written. */
export function planWhatsAppHref(message: string): string {
  return `${WHATSAPP_URL}?text=${encodeURIComponent(message)}`;
}

export function EmptyCityHelp({
  lang,
  citySlug,
  cityName,
  categoryName,
  categorySlug,
}: EmptyCityHelpProps) {
  const { t } = useTranslation();
  const isRtl = lang === "ar";
  const showDinnerShows = categorySlug === "restaurants" && DINNER_SHOW_CITIES.has(citySlug);
  const title = categoryName
    ? t("activity.empty_city.title_combo", { category: categoryName, city: cityName })
    : t("activity.empty_city.title", { city: cityName });

  return (
    <section
      aria-labelledby="empty-city-title"
      className="p-5 text-start sm:p-6"
      style={pewterPanelApproved(26)}
    >
      <h2
        id="empty-city-title"
        className="text-[20px] font-bold leading-tight sm:text-[22px]"
        style={{ color: PW.navy, fontFamily: "var(--font-geist)", letterSpacing: "-0.02em" }}
      >
        {title}
      </h2>
      <p className="mt-1.5 max-w-2xl text-[14px] leading-relaxed" style={{ color: PW.muted }}>
        {t("activity.empty_city.body")}
      </p>

      {showDinnerShows && (
        <Link
          to="/$lang/category/$category/$city"
          params={{ lang, category: "shows", city: citySlug }}
          // Carries the guest's dates and party to the shows (state preservation).
          search={(prev: Record<string, unknown>) => prev}
          className="mt-4 flex min-h-11 items-center justify-between gap-3 rounded-2xl px-4 py-3 no-underline transition hover:bg-white"
          style={{ background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}`, color: PW.navy }}
        >
          <span className="flex min-w-0 items-center gap-3">
            <Icon
              name="ri-restaurant-2-line"
              className="shrink-0 text-[20px]"
              style={{ color: PW.goldDark }}
              aria-hidden="true"
            />
            <span className="min-w-0">
              <span className="block text-[14.5px] font-bold">
                {t("activity.empty_city.dinner_show_title", { city: cityName })}
              </span>
              <span className="mt-0.5 block text-[13px] font-semibold" style={{ color: PW.muted }}>
                {t("activity.empty_city.dinner_show_cta")}
              </span>
            </span>
          </span>
          <Icon
            name={isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line"}
            className="shrink-0 text-[18px]"
            style={{ color: PW.goldDark }}
            aria-hidden="true"
          />
        </Link>
      )}

      <Suspense fallback={null}>
        <NearbyRail lang={lang} citySlug={citySlug} includeSelf={!!categoryName} />
      </Suspense>

      <div className="mt-5 flex flex-wrap gap-2">
        <a
          href={planWhatsAppHref(t("activity.empty_city.whatsapp_message", { city: cityName }))}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-[14px] font-semibold transition hover:opacity-95"
          style={pewterPrimary}
        >
          <Icon name="ri-whatsapp-line" className="text-[16px]" aria-hidden="true" />
          {t("activity.empty_city.plan_cta", { city: cityName })}
        </a>
        <Link
          to="/$lang/ai-travel-planner"
          params={{ lang }}
          search={{ q: t("activity.empty_city.ai_question", { city: cityName }) }}
          className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-[14px] font-semibold transition hover:bg-white"
          style={{ background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}`, color: PW.navy }}
        >
          <Icon name="ri-sparkling-2-line" className="text-[16px]" style={{ color: PW.goldDark }} aria-hidden="true" />
          {t("activity.empty_city.ai_cta")}
        </Link>
      </div>
    </section>
  );
}

function NearbyRail({
  lang,
  citySlug,
  includeSelf,
}: {
  lang: string;
  citySlug: string;
  includeSelf: boolean;
}) {
  const { t } = useTranslation();
  const { data: cities } = useSuspenseQuery(emptyHubCitiesQueryOptions(lang));
  const { cities: sources } = pickNearbyBookableCities(cities, citySlug, {
    includeSelf,
    want: RAIL_MAX,
  });
  const results = useSuspenseQueries({
    queries: sources.map((city) => nearbyRailQueryOptions(city.slug, lang)),
  });

  // Nearest city first, then the next, never the same activity twice.
  const seen = new Set<string>();
  const items: NearbyRailItem[] = [];
  for (const result of results) {
    for (const item of result.data) {
      if (items.length >= RAIL_MAX) break;
      if (seen.has(item.slug)) continue;
      seen.add(item.slug);
      items.push(item);
    }
  }
  if (items.length === 0) return null;

  return (
    <div className="mt-5">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: PW.muted }}>
        {t("activity.empty_city.rail_title")}
      </h3>
      {/*
        A scroll row clips on both axes, which would slice the card's shadow
        flat. The padding gives the shadow room and the negative margin hands
        that room back to the layout (same trick as CityCategoryGroups).
      */}
      <ul className="-mx-4 -mb-6 -mt-5 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-8 pt-8 sm:gap-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((a) => (
          <li key={a.id} className="w-[210px] shrink-0 snap-start sm:w-[230px] lg:w-[250px]">
            <ActivityCardDiscover
              slug={a.slug}
              images={a.images}
              title={a.title}
              city={a.city.name}
              citySlug={a.city.slug}
              category={a.category?.name ?? ""}
              categorySlug={a.category?.slug}
              segment={a.segment}
              tags={(a as { tags?: readonly string[] | null }).tags ?? null}
              mode={a.mode}
              price={a.price}
              isNew={a.isNew === true}
              typePrice={a.typePrice}
              rating={a.rating}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
